import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiSave, FiPackage, FiArrowDown, FiArrowUp, FiInfo, FiAlertCircle } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import {
  loadInventoryMovementConfig,
  saveInventoryMovementConfig,
  DEFAULT_INVENTORY_MOVEMENT_CONFIG,
  INVENTORY_MOVEMENT_IN_OPTIONS,
  INVENTORY_MOVEMENT_OUT_OPTIONS,
  ALWAYS_IN_DOCS,
  ALWAYS_OUT_DOCS,
} from '../../utils/inventoryMovementHelper';

const InventoryMovementSettings = () => {
  const { activeCompany, currentUser } = useAuth();
  const { canEdit } = usePagePermissions('settings', 'inventoryMovement');

  const [config, setConfig] = useState({ ...DEFAULT_INVENTORY_MOVEMENT_CONFIG });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const saved = await loadInventoryMovementConfig(activeCompany.id);
      setConfig(saved);
    } catch (e) {
      setAlert({ type: 'error', message: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canEdit) { setAlert({ type: 'error', message: 'You do not have permission to save changes.' }); return; }
    setSaving(true);
    try {
      await saveInventoryMovementConfig(activeCompany.id, config, currentUser?.uid);
      setAlert({ type: 'success', message: 'Inventory movement settings saved. Affects new documents only.' });
    } catch (err) {
      console.error(err);
      setAlert({ type: 'error', message: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout pageTitle="Inventory Movement">
      <Container>
        <PageHeader>
          <PageTitle>Inventory Movement Settings</PageTitle>
          <PageDescription>
            Choose which document actually moves stock in your company. Only one document per direction can post to inventory.
          </PageDescription>
        </PageHeader>

        <WarningBanner>
          <FiAlertCircle />
          <div>
            <strong>Heads up:</strong> changes here apply to <b>new documents only</b>. Existing stock movements are not affected.
          </div>
        </WarningBanner>

        {alert && (
          <Alert $type={alert.type}>
            <FiInfo />
            {alert.message}
          </Alert>
        )}

        {loading ? (
          <Card><CardBody>Loading…</CardBody></Card>
        ) : (
          <form onSubmit={handleSave}>
            {/* ── Inventory In ── */}
            <Card>
              <CardHeader>
                <CardIcon $accent={colors.success.main}><FiArrowDown /></CardIcon>
                <div>
                  <CardTitle>Inventory In</CardTitle>
                  <CardSub>When should stock be added to inventory on a purchase?</CardSub>
                </div>
              </CardHeader>
              <CardBody>
                <RadioList>
                  {INVENTORY_MOVEMENT_IN_OPTIONS.map(opt => (
                    <RadioRow
                      key={opt.value}
                      $active={config.inMode === opt.value}
                      onClick={() => canEdit && setConfig(c => ({ ...c, inMode: opt.value }))}
                    >
                      <RadioDot $active={config.inMode === opt.value} />
                      <div>
                        <RadioLabel>{opt.label}</RadioLabel>
                        <RadioDesc>{opt.description}</RadioDesc>
                      </div>
                    </RadioRow>
                  ))}
                </RadioList>

                <RefTitle>Always-on (not configurable):</RefTitle>
                <RefList>
                  {ALWAYS_IN_DOCS.map(d => (
                    <RefPill key={d.code}><FiPackage size={11} /> {d.label}</RefPill>
                  ))}
                </RefList>
              </CardBody>
            </Card>

            {/* ── Inventory Out ── */}
            <Card>
              <CardHeader>
                <CardIcon $accent={colors.error.main}><FiArrowUp /></CardIcon>
                <div>
                  <CardTitle>Inventory Out</CardTitle>
                  <CardSub>When should stock be deducted from inventory on a sale?</CardSub>
                </div>
              </CardHeader>
              <CardBody>
                <RadioList>
                  {INVENTORY_MOVEMENT_OUT_OPTIONS.map(opt => (
                    <RadioRow
                      key={opt.value}
                      $active={config.outMode === opt.value}
                      onClick={() => canEdit && setConfig(c => ({ ...c, outMode: opt.value }))}
                    >
                      <RadioDot $active={config.outMode === opt.value} />
                      <div>
                        <RadioLabel>{opt.label}</RadioLabel>
                        <RadioDesc>{opt.description}</RadioDesc>
                      </div>
                    </RadioRow>
                  ))}
                </RadioList>

                <RefTitle>Always-on (not configurable):</RefTitle>
                <RefList>
                  {ALWAYS_OUT_DOCS.map(d => (
                    <RefPill key={d.code}><FiPackage size={11} /> {d.label}</RefPill>
                  ))}
                </RefList>
              </CardBody>
            </Card>

            {canEdit && (
              <ActionBar>
                <SaveButton type="submit" disabled={saving}>
                  <FiSave />
                  {saving ? 'Saving…' : 'Save Settings'}
                </SaveButton>
              </ActionBar>
            )}
          </form>
        )}
      </Container>
    </PageLayout>
  );
};

export default InventoryMovementSettings;

// ─── Styled ───────────────────────────────────────────────────────────────────
const Container = styled.div`max-width: 900px; margin: 0 auto;`;
const PageHeader = styled.div`margin-bottom: 16px;`;
const PageTitle = styled.h1`font-size: 22px; font-weight: 700; color: ${colors.text.primary}; margin: 0 0 6px 0;`;
const PageDescription = styled.p`font-size: 13px; color: ${colors.text.secondary}; margin: 0;`;

const WarningBanner = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  padding: 12px 16px;
  background: ${colors.warning.main}15;
  border: 1px solid ${colors.warning.main}40;
  border-radius: 8px;
  color: ${colors.text.primary};
  font-size: 13px;
  margin-bottom: 16px;
  svg { color: ${colors.warning.main}; flex-shrink: 0; margin-top: 2px; }
`;

const Alert = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px;
  background: ${p => p.$type === 'error' ? `${colors.error.main}15` : `${colors.success.main}15`};
  border: 1px solid ${p => p.$type === 'error' ? `${colors.error.main}40` : `${colors.success.main}40`};
  border-radius: 8px;
  color: ${p => p.$type === 'error' ? colors.error.main : colors.success.main};
  font-size: 13px;
  margin-bottom: 16px;
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 10px;
  margin-bottom: 16px;
  overflow: hidden;
`;
const CardHeader = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;
const CardIcon = styled.div`
  width: 36px; height: 36px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  background: ${p => p.$accent}15; color: ${p => p.$accent};
`;
const CardTitle = styled.h2`font-size: 15px; font-weight: 600; color: ${colors.text.primary}; margin: 0;`;
const CardSub = styled.div`font-size: 12px; color: ${colors.text.secondary}; margin-top: 2px;`;
const CardBody = styled.div`padding: 18px;`;

const RadioList = styled.div`display: flex; flex-direction: column; gap: 10px;`;
const RadioRow = styled.div`
  display: flex; align-items: flex-start; gap: 12px;
  padding: 12px 14px;
  border: 2px solid ${p => p.$active ? colors.primary.main : colors.neutral[200]};
  background: ${p => p.$active ? `${colors.primary.main}08` : 'white'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: ${colors.primary.main}80; }
`;
const RadioDot = styled.div`
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid ${p => p.$active ? colors.primary.main : colors.neutral[400]};
  background: white;
  position: relative;
  margin-top: 2px;
  flex-shrink: 0;
  &::after {
    content: '';
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: ${p => p.$active ? colors.primary.main : 'transparent'};
  }
`;
const RadioLabel = styled.div`font-size: 14px; font-weight: 600; color: ${colors.text.primary}; margin-bottom: 3px;`;
const RadioDesc = styled.div`font-size: 12px; color: ${colors.text.secondary}; line-height: 1.4;`;

const RefTitle = styled.div`
  font-size: 11px; font-weight: 600; color: ${colors.text.secondary};
  text-transform: uppercase; letter-spacing: 0.4px;
  margin-top: 18px; margin-bottom: 8px;
`;
const RefList = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;
const RefPill = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px;
  background: ${colors.neutral[100]};
  color: ${colors.text.secondary};
  border-radius: 12px;
  font-size: 11px;
`;

const ActionBar = styled.div`display: flex; justify-content: flex-end; padding: 4px 0 24px 0;`;
const SaveButton = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main}; color: white;
  border: none; border-radius: 8px;
  font-size: 14px; font-weight: 600; cursor: pointer;
  &:hover:not(:disabled) { background: ${colors.primary.dark}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
